/**
 * Calcula todos los pasos del algoritmo de división larga tradicional.
 * Soporta divisor > dividendo generando decimales (máx 2 dígitos decimales).
 * Soporta dividendo y divisor con punto decimal.
 *
 * @param {string} dividendoStr  - Ej: "24" o "2.5"
 * @param {string} divisorStr   - Ej: "50" o "0.5"
 * @returns {DivisionData | null}
 */
export function calcularDivision(dividendoStr, divisorStr) {
  // Normalizar: eliminar puntos finales como "2." → "2"
  const dStr = dividendoStr.replace(/\.$/, '');
  const rStr = divisorStr.replace(/\.$/, '');

  const dividendo = parseFloat(dStr);
  const divisor = parseFloat(rStr);

  if (
    !dStr || !rStr ||
    isNaN(dividendo) || isNaN(divisor) ||
    divisor === 0
  ) {
    return null;
  }

  // Convertir ambos a enteros multiplicando por la mayor potencia de 10 necesaria
  // para eliminar decimales. El cociente no cambia: (a*k)/(b*k) = a/b
  const decDividendo = (dStr.split('.')[1] || '').length;
  const decDivisor = (rStr.split('.')[1] || '').length;
  const factor = Math.max(decDividendo, decDivisor);
  const mult = Math.pow(10, factor);

  // Trabajo con enteros para la división larga
  const divisorEntero = Math.round(divisor * mult);

  // Cadena del dividendo sin punto: "0.5" → "05", "2.5" → "25", "124" → "124"
  // Preserva ceros iniciales para que la visualización sea correcta.
  const dividendoEnteroStr = dStr.replace('.', '');
  const digitosDividendo = dividendoEnteroStr.split('');

  // --- Construir dígitos del cociente con algoritmo paso a paso ---
  // Manejamos decimales: si en algún punto el acumulado < divisorEntero, multiplicamos x10
  // y marcamos que el cociente tendrá punto decimal.

  const pasos = [];
  let acumulado = 0;
  let acumulandoDecimales = false;
  let decimalesGenerados = 0;
  const MAX_DECIMALES = 2;

  // Dígitos que procesaremos: los del dividendo + dígitos decimales extra (ceros)
  // Representamos los dígitos del cociente como array
  const digitosCociente = [];

  // Estado de columna: cada "dígito del dividendo" ocupa una columna
  // Las columnas decimales se agregan después del punto
  let colActual = 0;
  let colInicioGrupo = 0;

  // Fase 1: procesar dígitos enteros del dividendo
  for (let i = 0; i < digitosDividendo.length; i++) {
    acumulado = acumulado * 10 + parseInt(digitosDividendo[i], 10);

    if (acumulado < divisorEntero) {
      // No alcanza: cociente parcial = 0 (solo si ya emitimos al menos un dígito)
      if (digitosCociente.length > 0) {
        digitosCociente.push('0');
        pasos.push({
          parcial: String(acumulado),
          producto: '0',
          residuo: String(acumulado),
          colInicio: colInicioGrupo,
          colFin: i,
          esDecimal: false,
        });
        colInicioGrupo = i + 1;
      }
      // Si aún no hay dígitos, simplemente acumulamos (el divisor no cabe todavía)
      colActual = i + 1;
      continue;
    }

    const digCoc = Math.floor(acumulado / divisorEntero);
    const producto = digCoc * divisorEntero;
    const residuo = acumulado - producto;

    digitosCociente.push(String(digCoc));
    pasos.push({
      parcial: String(acumulado),
      producto: String(producto),
      residuo: String(residuo),
      colInicio: colInicioGrupo,
      colFin: i,
      esDecimal: false,
    });

    acumulado = residuo;
    colInicioGrupo = i + 1;
    colActual = i + 1;
  }

  // Si no se generó ningún dígito de cociente (divisor > dividendo completo)
  // entonces el cociente empieza con 0 y tenemos que generar decimales
  if (digitosCociente.length === 0) {
    digitosCociente.push('0');
    // No generamos paso para el 0 entero, solo marcamos que hay decimales
    acumulandoDecimales = true;
  }

  // Fase 2: si hay residuo y no llegamos a MAX_DECIMALES, extender con decimales
  const tienePuntoDecimal = acumulado > 0 || acumulandoDecimales;
  let puntoDotCol = colActual; // columna donde va el punto decimal en el cociente

  if (tienePuntoDecimal) {
    while (acumulado > 0 && decimalesGenerados < MAX_DECIMALES) {
      acumulado *= 10;
      const digCoc = Math.floor(acumulado / divisorEntero);
      const producto = digCoc * divisorEntero;
      const residuo = acumulado - producto;

      digitosCociente.push(String(digCoc));
      pasos.push({
        parcial: String(acumulado),
        producto: String(producto),
        residuo: String(residuo),
        colInicio: colInicioGrupo,
        colFin: colActual + decimalesGenerados,
        esDecimal: true,
      });

      acumulado = residuo;
      decimalesGenerados++;
      colInicioGrupo = colActual + decimalesGenerados;
    }
  }

  // Total de columnas = columnas del dividendo + columnas decimales extra
  const numColsDividendo = digitosDividendo.length;
  const numColsExtra = decimalesGenerados; // columnas de "bajada" de ceros

  const cocienteStr = digitosCociente.join('');
  const cocienteConPunto = tienePuntoDecimal && decimalesGenerados > 0
    ? cocienteStr.slice(0, cocienteStr.length - decimalesGenerados) +
      '.' +
      cocienteStr.slice(cocienteStr.length - decimalesGenerados)
    : cocienteStr;

  return {
    dividendo: dividendoStr,
    divisor: divisorStr,
    dividendoEnteroStr,     // cadena del dividendo sin punto (para la grid interna)
    cociente: cocienteConPunto,
    digitosCociente,        // array sin punto, para renderizar celda a celda
    puntoDotCol,            // columna después de la cual va el punto decimal en cociente
    tieneDecimales: tienePuntoDecimal && decimalesGenerados > 0,
    decimalesGenerados,
    residuoFinal: String(acumulado),
    pasos,
    numColsDividendo,
    numColsExtra,
    totalCols: numColsDividendo + numColsExtra,
    decDividendo,  // decimales del dividendo original (0 si ninguno)
    decDivisor,    // decimales del divisor original (0 si ninguno)
  };
}

/**
 * Genera la estructura de celdas para el DivisionGrid.
 * Retorna: { filaCociente, filaDividendo, filaPasos, divisor, totalCols, tieneDecimales, puntoDotCol }
 */
export function generarCeldas(data) {
  if (!data) return null;

  const {
    dividendo, divisor, dividendoEnteroStr, digitosCociente, pasos,
    numColsDividendo, totalCols, tieneDecimales, puntoDotCol, decimalesGenerados,
  } = data;

  // Usar la cadena entera (sin punto) para construir la grid de dígitos.
  // dividendoEnteroStr existe cuando el dividendo tiene decimales; si no, usar dividendo.
  const digitosDividendo = (dividendoEnteroStr || dividendo).split('');

  // --- Fila cociente ---
  // Los dígitos del cociente se alinean a la derecha de las columnas del dividendo
  // Si hay decimales, el cociente tiene más dígitos que columnas del dividendo.
  // Cociente siempre empieza en la columna 0 (alineado con primer dígito del dividendo)
  // pero si el divisor no cabe en los primeros dígitos, el 0 va en col 0.
  const filaCociente = buildEmptyRow(totalCols);
  // Los dígitos del cociente ocupan las columnas 0..numColsDividendo-1 (enteros)
  // y colsDividendo..totalCols-1 (decimales)
  const numDigEnteros = digitosCociente.length - decimalesGenerados;
  const offsetEntero = numColsDividendo - numDigEnteros;

  digitosCociente.forEach((d, i) => {
    const col = i < numDigEnteros
      ? offsetEntero + i                    // dígitos enteros del cociente
      : numColsDividendo + (i - numDigEnteros); // dígitos decimales
    if (col >= 0 && col < totalCols) {
      filaCociente[col] = { valor: d, tipo: 'digit' };
    }
  });

  // --- Fila dividendo ---
  // Columnas 0..numColsDividendo-1 con los dígitos, el resto vacío
  const filaDividendo = buildEmptyRow(totalCols);
  digitosDividendo.forEach((d, i) => {
    filaDividendo[i] = { valor: d, tipo: 'digit' };
  });

  // --- Filas de pasos ---
  const filaPasos = [];

  pasos.forEach((paso) => {
    const filaResta = buildEmptyRow(totalCols);
    const digProducto = paso.producto.split('');
    // Alinear a la derecha dentro del rango colInicio..colFin
    const rangeLen = paso.colFin - paso.colInicio + 1;
    const offset = paso.colFin - digProducto.length + 1;
    digProducto.forEach((d, i) => {
      const col = offset + i;
      if (col >= 0 && col < totalCols) {
        filaResta[col] = {
          valor: d,
          tipo: 'digit',
          borderTop: i === 0,  // línea encima de la primera celda del grupo
          borderTopFull: true, // la línea se extiende a todos los del grupo
        };
      }
    });
    // Marcar borderTop para todas las celdas del rango que tengan valor
    // pero solo visualmente desde la primera con valor hasta colFin
    let firstValCol = -1;
    for (let c = 0; c < totalCols; c++) {
      if (filaResta[c].valor) { firstValCol = c; break; }
    }
    if (firstValCol >= 0) {
      for (let c = firstValCol; c <= paso.colFin && c < totalCols; c++) {
        filaResta[c].borderTop = true;
      }
    }
    filaPasos.push({ celdas: filaResta, esResta: true, tieneMenos: true });

    // Fila de residuo: muestra el parcial del siguiente paso (número completo con el dígito bajado)
    // En el último paso muestra el residuo final
    const filaResiduo = buildEmptyRow(totalCols);
    const pasoIdx = pasos.indexOf(paso);
    const siguientePaso = pasos[pasoIdx + 1];
    const esUltimo = pasoIdx === pasos.length - 1;

    if (esUltimo) {
      // Último paso: mostrar residuo final (incluyendo 0 para indicar división exacta)
      const digResiduo = paso.residuo.split('');
      const resOffset = paso.colFin - digResiduo.length + 1;
      digResiduo.forEach((d, i) => {
        const col = resOffset + i;
        if (col >= 0 && col < totalCols) {
          filaResiduo[col] = { valor: d, tipo: 'digit' };
        }
      });
    } else if (siguientePaso) {
      // Pasos intermedios: mostrar el parcial del siguiente paso (número con dígito bajado)
      const digParcial = siguientePaso.parcial.split('');
      const parcialOffset = siguientePaso.colFin - digParcial.length + 1;
      digParcial.forEach((d, i) => {
        const col = parcialOffset + i;
        if (col >= 0 && col < totalCols) {
          filaResiduo[col] = { valor: d, tipo: 'digit' };
        }
      });
    }
    filaPasos.push({ celdas: filaResiduo, esResta: false });
  });

  return {
    filaCociente,
    filaDividendo,
    filaPasos,
    divisor,
    totalCols,
    tieneDecimales,
    puntoDotCol,
    numColsDividendo,
  };
}

function buildEmptyRow(n) {
  return Array.from({ length: n }, () => ({
    valor: '', tipo: 'empty', borderTop: false,
  }));
}
